/**
 * FloorCalendar — stage A of the PM-directed EXACT-UX port (2026-08-08, Greg):
 * the screen now renders the Manager Calendar prototype ITSELF (a patched copy
 * at app/public/floor-board/board.dc.html, chromeless), so layout, brand and
 * behavior are the prototype's own, byte-faithful — deliberately NOT the app
 * design system (sanctioned exception, docs/design-baseline.md).
 *
 * The serving path still holds: this screen fetches the day through the same
 * adapter ops as before (useFloorCalendarDay) and POSTS the mapped tenant shape
 * (toDCData) into the board; the board's studio:navigate events relay back and
 * become real router navigation (Event Day / Concierge). Board-internal writes
 * are prototype-local this stage; they swap to real ops as the verbatim TSX
 * port replaces the embed screen-by-screen (stages B+).
 *
 * Location (JAI-1935, 2026-10-05): the board reads ONE explicit location — the
 * shared active-location selection (the topbar picker, or the picker on this
 * screen), checked against the tenant's complete served catalog. Nothing is
 * read until that location is valid; no default id, first row, sort order or
 * zone is ever guessed. Every read is issued under an explicit request scope
 * (confirmed tenant + generation + location id) that the response echoes, so a
 * day read for one scope never speaks for another — even when the location id
 * and date are unchanged. The host feedback around the board is the app design
 * system (Banner, Button, Dropdown, Skeleton, EmptyState).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { T, Banner, Button, Dropdown, Skeleton } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { EmptyState } from "../../shell/composites";
import { useNav } from "../../shell/router";
import { useActiveLocationGate, type ServedLocation } from "../../shell/active-location";
import { useT } from "../../lib/i18n";
import { staffKey, toDCData, useFloorCalendarDay, cancelAppointment, checkInAppointment, markAppointmentNoShow, moveQueueDuty, addCivilDays, civilTodayInZone, daysBetweenCivil, floorRequestScope, isFloorDayFor, isValidIanaZone } from "./floor-calendar-data";
import { rescheduleAppointment, bookAppointment } from "../appointments/appointments-api";
import { listEventStaffing, assignEventRole } from "../workforce/wf-api";
import { calendarDay } from "@v0y4ge/api-client";
import { addQueueEntry } from "../queues/queues-api";

const hm = (min: number) => String(Math.floor(min / 60)).padStart(2, "0") + ":" + String(min % 60).padStart(2, "0");

const browserBootstrapToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** Visually hidden, still announced — the skeleton itself is aria-hidden. */
const srOnly: CSSProperties = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

/** The served, active locations as the design system's single-select. The
 * choice is published to the shared selection, exactly as the topbar's is. */
function LocationPicker({ locations, value, onPick, label, ariaLabel, placeholder, size = "md" }: {
  locations: ServedLocation[]; value: string | null; onPick: (id: string) => void;
  label?: string; ariaLabel?: string; placeholder: string; size?: "sm" | "md";
}) {
  return (
    <Dropdown testid="floor-location-picker" options={locations.map((l) => ({ value: l.id, label: l.name }))}
      value={value} onChange={onPick} label={label} aria-label={label ? undefined : ariaLabel}
      placeholder={placeholder} size={size} portal style={{ width: "100%", maxWidth: 360 }} />
  );
}

export default function FloorCalendar() {
  const t = useT();
  const nav = useNav();
  /* The ops-board catalog purpose (PR2859 policy A): listOpsBoardLocations under
   * a wfops.read tenant grant, never the settings list behind locations.read. */
  const locGate = useActiveLocationGate("ops-board");
  const { gate } = locGate;
  /* The one location this board reads: a served, active location someone chose.
   * Null in every other gate state, and then the day hook requests nothing.
   * Named so the hook call and every isFloorDayFor check share ONE identity. */
  const requestedLocationId: string | null = gate.kind === "ready" ? gate.location.id : null;
  const locationReady = requestedLocationId != null;
  /* Every read, response, write and board message is scoped to tenant +
   * generation + location. The same key is the hook's request scope (echoed on
   * every FloorDay as `requestScope`), so a tenant generation switch under the
   * same location id and date re-reads, and the old generation's day is never
   * re-anchored, posted, written against or navigated from. The ref is the
   * LATEST scope, set during render, read after each await in a write. */
  const scopeKey = floorRequestScope(locGate.tenantKey, locGate.generation, requestedLocationId);
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const [dateISO, setDateISO] = useState(browserBootstrapToday);
  /* The board owns which day is on screen; the host follows it so every day's
   * served families are read for THAT day rather than pinning today. */
  const [dayOff, setDayOff] = useState(0);
  const dateForDay = useMemo(() => addCivilDays(dateISO, dayOff), [dayOff, dateISO]);
  const { day, loading, error, reload } = useFloorCalendarDay(dateForDay, requestedLocationId, { enabled: locationReady, scope: scopeKey });
  /* A response counts only for the scope on screen (tenant, generation AND
   * location, as echoed by the hook), and only once the board echoed that same
   * location back. Before both, it may not re-anchor the dates, post, write or
   * navigate — a day read for A never speaks for B, nor generation 1 for 2. */
  const scopedDay = locationReady && day && day.requestedLocationId === requestedLocationId && (day.requestScope ?? null) === scopeKey ? day : null;
  const echoError = scopedDay && scopedDay.locationId !== requestedLocationId ? t("floorCal.locationEchoBody", "The calendar didn't confirm the selected location, so this day isn't shown. Retry, or choose the location again.") : null;
  const anchorDay = scopedDay && !echoError ? scopedDay : null;
  const zoneError = anchorDay && !isValidIanaZone(anchorDay.tz) ? t("floorCal.timezoneInvalid", "This branch did not return a valid IANA time zone. Calendar dates are unavailable until the branch time zone is configured.") : null;
  /* `dateISO` is the board's offset-0 date and must equal the BRANCH's civil
   * Today before anything is posted. `day` must also be the response for the
   * date AND scope on screen: the hook keeps returning the previous query's
   * day while a new date loads, so a re-anchor or a board day change would
   * otherwise post (or write, or navigate) against the old day's rows. */
  const branchToday = anchorDay && !zoneError ? civilTodayInZone(new Date(), anchorDay.tz as string) : null;
  const anchorPending = !!branchToday && branchToday !== dateISO;
  const boardDay = !zoneError && !anchorPending && isFloorDayFor(anchorDay, dateForDay, requestedLocationId, scopeKey) ? anchorDay : null;
  /* The first response FOR THIS SCOPE is the authority for the branch. If
   * the browser bootstrap is on another civil date, re-anchor: Today stays
   * Today, and an explicit navigation keeps the civil date it was showing (the
   * next floor-data carries the rebased `off`, which the board adopts with the
   * new anchor). */
  useEffect(() => {
    if (!anchorDay || zoneError) return;
    const branchNow = civilTodayInZone(new Date(), anchorDay.tz as string);
    if (branchNow === dateISO) return;
    if (dayOff !== 0) setDayOff(dayOff + daysBetweenCivil(branchNow, dateISO));
    setDateISO(branchNow);
  }, [anchorDay, dayOff, dateISO, zoneError]);
  const loadingTitle = t("floorCal.loadingTitle", "Loading the calendar");
  const tzLoadingMsg = t("floorCal.timezoneLoading", "Aligning the board to the branch time zone…");
  const dayLoadingMsg = t("floorCal.dayLoading", "Loading this day's calendar…");
  const initialLoadingMsg = t("floorCal.loading", "Loading the board…");
  const refreshingMsg = t("floorCal.refreshing", "Refreshing this day's calendar…");
  const zoneTitle = t("floorCal.timezoneMissing", "Branch time zone unavailable");
  const failTitle = t("floorCal.pending", "The board couldn't load");
  /* The user-facing message never carries the server's error text: `error` is
   * per-op diagnostics (op names, statuses) that DataSourceStrip already owns. */
  const failBody = t("floorCal.loadFailedBody", "Check your connection, then retry. If it keeps happening, the branch calendar service may be unavailable.");
  const retryLabel = t("common.retry", "Retry");
  const notReadyMsg = t("floorCal.writeNotReady", "The board is still loading this day, so that change was not saved. Try again once it has loaded.");
  const eventLinkTitle = t("floorCal.eventLinkMissingTitle", "This event can't be opened in Event Day");
  const eventLinkBody = t("floorCal.eventLinkMissingBody", "The events list has no single event that matches this one on this day, so the calendar won't guess which event to open.");
  const notReadyNavTitle = t("floorCal.navNotReadyTitle", "This day is still loading");
  const notReadyNavBody = t("floorCal.navNotReadyBody", "Nothing was opened. Try again once the calendar has loaded this day.");
  const linkingFailTitle = t("floorCal.eventLinkingFailedTitle", "Event Day links are unavailable for this day");
  const linkingFailBody = t("floorCal.eventLinkingFailedBody", "The calendar couldn't confirm these events against the events list, so they won't open in Event Day. Retry to check again.");
  const echoTitle = t("floorCal.locationEchoTitle", "Location not confirmed");
  const locationsLoadingMsg = t("floorCal.locationsLoading", "Loading locations…");
  const locationPendingMsg = t("floorCal.locationPending", "Waiting for a location…");
  const locationLabel = t("floorCal.locationLabel", "Location");
  const locationPlaceholder = t("floorCal.locationPlaceholder", "Choose a location");
  /* Feedback for the LAST open request from the board, kept apart from the
   * board-wide event-linking state: "miss" = no single canonical event has that
   * id on the day on screen; "notReady" = the day on screen is not the current
   * query's response yet. Cleared on a successful open or a query change. */
  const [linkNotice, setLinkNotice] = useState<null | "miss" | "notReady">(null);
  useEffect(() => { setLinkNotice(null); }, [dateForDay]);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  /* board-local block id → served block id, for deletes of blocks created this session */
  const blockIdsRef = useRef<Record<string, string>>({});
  const [boardReady, setBoardReady] = useState(false);
  /* Public assets resolve through __STUDIO_ASSET_BASE__ (app/public), same
   * mechanism as JrniMark — a route-relative src would resolve against
   * /floor/calendar and 404. Falls back to the Vite site-root convention. */
  const boardSrc = useMemo(() => {
    const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
    return (base ? base.replace(/\/$/, "") + "/" : "/") + "floor-board/board.dc.html?chromeless=1";
  }, []);

  const post = useCallback((msg: Record<string, unknown>) => {
    frameRef.current?.contentWindow?.postMessage(msg, window.location.origin);
  }, []);
  /* The anchor is the authority for every date the board draws: its Today,
   * labels and day offsets are civil dates from `anchor.today` in `anchor.tz`. */
  const push = useCallback(() => {
    if (!boardDay) return;
    post({ type: "floor-data", data: { ...toDCData(boardDay, dayOff), anchor: { today: dateISO, tz: boardDay.tz }, off: dayOff } });
  }, [boardDay, dayOff, dateISO, post]);

  /* ── stage C wave 1 (2026-08-10): the board posts write INTENTS after applying
   * its exact local behavior; this host calls the REAL op (the waves-1–4 wiring),
   * reloads the served day on success, and answers floor-write-error on failure
   * so the board can say so. "ap{n}" entry ids resolve through the payload's
   * bookIds (same order as the pushed rows). Manager-created entries have no
   * served identity yet (booking write is a later wave). */
  const writeBack = useCallback(async (m: { op?: string; id?: string; localId?: string; next?: string; off?: number; from?: number; to?: number; dur?: number; cancel?: boolean; name?: string; who?: string; what?: string; service?: string; queueId?: string; staffIdx?: number; eventId?: string; room?: string; staffName?: string; purpose?: string }) => {
    /* The scope this write was issued under. After every await, a write whose
     * tenant, generation or location has since moved maps nothing, reloads
     * nothing and reports nothing to a board now showing another scope. */
    const myScope = scopeKey;
    const stale = () => scopeRef.current !== myScope;
    const fail = (message: string) => { if (!stale()) post({ type: "floor-write-error", message }); };
    /* Row ids ("ap3", "qd1", staff indexes) only mean something against the day
     * the board is showing. Until that day — this date, this scope — has
     * loaded, nothing is written. */
    if (!isFloorDayFor(boardDay, dateForDay, requestedLocationId, scopeKey)) return fail(echoError || zoneError || notReadyMsg);
    const day = boardDay;
    const payload = toDCData(day, dayOff) as { bookIds?: string[]; dutyIds?: (string | null)[]; book?: unknown[][] };
    const servedId = (entryId: string): string | null => {
      if (!entryId.startsWith("ap")) return null;
      return (payload.bookIds || [])[Number(entryId.slice(2))] ?? null;
    };
    const staffOf = (i?: number) => (i != null && day ? day.staff[i] ?? null : null);
    const dateOf = (off?: number) => addCivilDays(dateISO, Number(off) || 0);
    try {
      if (m.op === "apptStatus") {
        const id = servedId(String(m.id || ""));
        if (!id) return fail("That entry was created on the board this session — it has no server booking to update yet");
        if (m.next === "now") { await checkInAppointment(id); }
        else if (m.next === "noshow") { await markAppointmentNoShow(id); }
        else { return fail("Revoking a status has no server operation yet — the change is local only (REQ-CAL-7)"); }
        if (stale()) return;
        reload();
        return;
      }
      if (m.op === "apptEdit") {
        const id = servedId(String(m.id || ""));
        if (!id) return fail("That entry was created on the board this session — it has no server booking to reschedule yet");
        const date = dateOf(m.off);
        await rescheduleAppointment(id, { date, start: hm(Number(m.from) || 0), end: hm((Number(m.from) || 0) + (Number(m.dur) || 30)), reason: "Rescheduled on the Floor Calendar" });
        if (stale()) return;
        reload();
        return;
      }
      if (m.op === "apptMove") {
        const id = servedId(String(m.id || ""));
        if (!id) return fail("That entry was created on the board this session — it has no server booking to move yet");
        const st = staffOf(m.staffIdx);
        await rescheduleAppointment(id, { date: dateForDay, start: hm(Number(m.from) || 0), end: hm((Number(m.from) || 0) + (Number(m.dur) || 30)), staffId: st?.staffId ?? null, staffName: st?.name ?? null, reason: "Moved on the Floor Calendar" });
        if (stale()) return;
        reload();
        return;
      }
      if (m.op === "book") {
        const st = staffOf(m.staffIdx);
        await bookAppointment({
          service: String(m.what || "Appointment"), date: dateOf(m.off),
          start: hm(Number(m.from) || 0), end: hm((Number(m.from) || 0) + (Number(m.dur) || 30)),
          staffId: st?.staffId ?? null, staffName: st?.name ?? null,
          locationId: day?.locationId ?? null,
          customer: { name: String(m.who || "Walk-in") }, origin: "floor-calendar",
        });
        /* The board drew an optimistic row the moment the manager saved; the served
         * day now carries the SAME booking too. The board retires its own local copy
         * when it recognises the served row (see the floor-data reconcile) — no ack
         * from here, so a booking the server does NOT return stays on the board
         * rather than vanishing (2026-09-03). */
        if (stale()) return;
        await reload();
        return;
      }
      if (m.op === "apptCancel") {
        /* Undo on the creation toast. The booking may have no board id (it was
         * created this session), so resolve it the way the board recognises its
         * own row: staff + start + duration + customer. Service name is NOT a
         * key — the server's vocabulary drifts from the board's. */
        const norm = (s: unknown) => String(s ?? "").trim().toLowerCase();
        const rows = payload.book || [];
        const i = rows.findIndex((r) =>
          Number(r[0]) === Number(m.staffIdx) && Number(r[1]) === Number(m.from) &&
          Number(r[2]) === Number(m.dur) && norm(r[3]) === norm(m.who));
        const id = i > -1 ? (payload.bookIds || [])[i] ?? null : null;
        if (!id) return fail("That booking never reached the server — it has been taken off the board only");
        await cancelAppointment(id, "Undone on the Floor Calendar");
        if (stale()) return;
        await reload();
        return;
      }
      if (m.op === "blockAdd") {
        const date = dateOf(m.off);
        const created = await calendarDay.createCalendarDayBlock({
          title: String(m.purpose || "Calendar block"),
          startsAt: date + "T" + hm(Number(m.from) || 0) + ":00",
          endsAt: date + "T" + hm((Number(m.from) || 0) + (Number(m.dur) || 30)) + ":00",
          /* the contract's block is room-keyed; a staff-column hold names its person */
          room: String(m.room || m.staffName || "Floor"),
          ...(day?.locationId ? { locationId: day.locationId } : {}),
          notes: m.staffName ? "Held for " + m.staffName + " (Floor Calendar)" : "Floor Calendar",
        });
        /* a block created for the previous scope never enters this one's map */
        if (stale()) return;
        if (m.localId && created?.id) blockIdsRef.current[m.localId] = String(created.id);
        reload();
        return;
      }
      if (m.op === "blockRemove") {
        const sid = blockIdsRef.current[String(m.localId || "")];
        if (!sid) return fail("That block predates this session or never saved — nothing to delete on the server");
        await calendarDay.deleteCalendarDayBlock(sid);
        if (stale()) return;
        delete blockIdsRef.current[String(m.localId || "")];
        reload();
        return;
      }
      if (m.op === "dutyMove") {
        const bid = String(m.id || "");
        if (!bid.startsWith("qd")) return fail("That duty block was added on the board this session — it has no served roster row yet");
        const sid = (payload.dutyIds || [])[Number(bid.slice(2))];
        if (!sid) return fail("That duty block has no served roster row — nothing to move on the server");
        const st = staffOf(m.staffIdx);
        await moveQueueDuty(String(sid), { staff: st?.name ?? undefined, from: hm(Number(m.from) || 0), to: hm(Number(m.to) || 0) });
        if (stale()) return;
        reload();
        return;
      }
      if (m.op === "eventAssign") {
        const ev = day?.events.find((e) => e.id === m.eventId || e.studioId === m.eventId || staffKey(e.title) === staffKey(String(m.eventId || "")));
        const st = staffOf(m.staffIdx);
        if (!ev || !st?.staffId) return fail("Couldn't resolve the event or the person to a served identity");
        const plans = await listEventStaffing(dateForDay, dateForDay, day?.locationId ?? undefined);
        if (stale()) return;
        const plan = (plans?.items || []).find((x) => x.eventId === ev.id || staffKey(x.title) === staffKey(ev.title));
        const role = plan?.roles?.find((r) => (r.assignees?.length ?? 0) < (r.count ?? 1)) ?? plan?.roles?.[0];
        if (!plan || !role) return fail("No staffing plan is served for " + ev.title + " — the assignment is local only");
        await assignEventRole(plan.eventId, role.id, st.staffId);
        if (stale()) return;
        reload();
        return;
      }
      if (m.op === "convert") {
        const q = day?.queues.find((x) => x.id === m.queueId) || day?.queues[0];
        if (!q) return fail("No served queue to join — the conversion is local only");
        await addQueueEntry(q.id, { name: String(m.name || "Walk-in"), service: m.service || null });
        if (stale()) return;
        if (m.cancel && m.id) {
          const id = servedId(String(m.id));
          if (id) { await cancelAppointment(id, "Converted to the walk-in queue"); if (stale()) return; }
          else fail("Queue joined, but the appointment was board-local — nothing to cancel on the server");
        }
        reload();
        return;
      }
    } catch (e) {
      fail(e instanceof Error ? e.message : String(e));
    }
  }, [boardDay, dayOff, dateISO, dateForDay, requestedLocationId, scopeKey, reload, echoError, zoneError, notReadyMsg, post]);

  useEffect(() => {
    if (!boardReady) return;
    /* No valid location yet: the board keeps no previous location's rows. */
    if (!locationReady) post({ type: "floor-day-loading", title: loadingTitle, message: locationPendingMsg });
    else if (echoError) post({ type: "floor-day-error", title: echoTitle, message: echoError });
    else if (zoneError) post({ type: "floor-timezone-error", title: zoneTitle, message: zoneError });
    else if (error && !day) post({ type: "floor-day-error", title: failTitle, message: failBody });
    else if (anchorPending) post({ type: "floor-timezone-loading", title: loadingTitle, message: tzLoadingMsg });
    else if (!boardDay) post({ type: "floor-day-loading", title: loadingTitle, message: dayLoadingMsg });
    else push();
  }, [boardReady, push, post, locationReady, echoError, echoTitle, locationPendingMsg, zoneError, error, day, anchorPending, boardDay, zoneTitle, failTitle, failBody, loadingTitle, tzLoadingMsg, dayLoadingMsg]);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      /* ORIGIN GATE — re-applied 2026-09-08 after a pack reverted it, and it is
         not optional. `floor-write` below dispatches ten authenticated
         mutations; an ungated listener lets ANY window holding a handle to
         Studio drive them, and `postMessage(_, "*")` leaks the board's data to
         whatever ends up in that frame. The board is served from our own origin,
         so both halves are an exact match rather than an allowlist. */
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as { type?: string; off?: number; detail?: { surface?: string; params?: Record<string, unknown> } } | null;
      if (!m || typeof m !== "object") return;
      if (m.type === "floor-board-ready") { setBoardReady(true); return; }
      if (m.type === "floor-day") { setDayOff(Number(m.off) || 0); setLinkNotice(null); return; }
      if (m.type === "floor-write") { void writeBack(m as unknown as Parameters<typeof writeBack>[0]); return; }
      if (m.type === "floor-nav" && m.detail) {
        const p = m.detail.params || {};
        /* The board's wire names Event Day `eventDay`; the host's route value is
         * `eventday`, which stays accepted as an alias. Normalized BEFORE the
         * not-ready guard so a genuine click on a loading day says so too. Any
         * other spelling is not a known surface and opens nothing. */
        const surface = m.detail.surface === "eventDay" || m.detail.surface === "eventday" ? "eventday" : m.detail.surface;
        /* Both jump-offs resolve against the CURRENT query's day only (this date,
         * this scope, echoed by the board). While a new query loads, `day` is
         * still the previous response, whose rows are not this day's — say so
         * rather than route. */
        if (!isFloorDayFor(boardDay, dateForDay, requestedLocationId, scopeKey)) {
          if (surface === "eventday" || surface === "concierge") setLinkNotice("notReady");
          return;
        }
        if (surface === "eventday") {
          /* Event Day reads Studio ids (sw_*) only. The board must name the
           * canonical id, and exactly one row of the day on screen must carry it
           * as its verified `studioId`. Never `e.id` (a WorkForce ops id), never
           * a title: an unknown, missing or ambiguous id opens nothing. */
          const pid = typeof p.eventId === "string" ? p.eventId.trim() : "";
          const hits = pid ? boardDay.events.filter((e) => e.studioId === pid) : [];
          if (hits.length === 1) { setLinkNotice(null); nav.go("eventday", { eventId: pid }); }
          else setLinkNotice("miss");
          return;
        }
        if (surface === "concierge") {
          const q = boardDay.queues.find((x) => String(p.queueId) === x.id || staffKey(x.name) === staffKey(String((p.queueName as string) || "")));
          nav.go("queues.console", q?.consoleId ? { q: q.consoleId } : {});
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [boardDay, dateForDay, requestedLocationId, scopeKey, nav, writeBack]);

  /* The skeleton covers the board until the FIRST day it can show has loaded.
   * That includes the branch re-anchor, where `day` is the provisional
   * bootstrap date's response (never posted) while the branch's own day is
   * still loading. Once a board day has been shown, later loads keep it visible. */
  const [boardDayShown, setBoardDayShown] = useState(false);
  useEffect(() => { if (boardDay) setBoardDayShown(true); }, [boardDay]);
  /* A new location, tenant or tenant generation starts clean: no open notice,
   * no block-id map and no "already shown" board carried over from before. */
  useEffect(() => {
    blockIdsRef.current = {};
    setLinkNotice(null);
    setBoardDayShown(false);
  }, [scopeKey]);
  /* The catalog is still answering (or no tenant is confirmed yet): the same
   * board-sized skeleton, since the board is what lands next. */
  const catalogPending = gate.kind === "noTenant" || gate.kind === "loading";
  const initialLoading = catalogPending || (locationReady && !boardDay && !boardDayShown && !error && !zoneError && !echoError && (loading || anchorPending));
  const refreshing = loading && !!boardDay;
  const linkingFailed = !!boardDay && !boardDay.eventLinking.ok;
  const retry = () => { void reload(); };
  const noticePad = { padding: `${T.space[2]} ${T.space[4]}` };

  return (
    <Screen rail="floor-calendar">
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <DataSourceStrip screen="floor.calendar" ops={["listAppointments", "getOpsBoard", "listQueueDuty", "listExternalBusy", "listResources"]} reqId="REQ-CAL-1" />
        {/* A ready board offers the location choice itself at every width: the
            header's picker reads the settings catalog (locations.read), which an
            ops-board role may not hold, so this screen's ops-board gate owns it. */}
        {gate.kind === "ready" ? (
          <div style={noticePad}>
            <LocationPicker locations={gate.locations} value={gate.location.id} onPick={locGate.select} ariaLabel={locationLabel} placeholder={locationPlaceholder} size="sm" />
          </div>
        ) : null}
        {locationReady && error && !day ? (
          <div style={noticePad}>
            <Banner tone="danger" title={failTitle} testid="floor-load-failed"
              action={<Button variant="secondary" size="sm" onClick={retry}>{retryLabel}</Button>}>
              {failBody}
            </Banner>
          </div>
        ) : null}
        {echoError ? (
          <div style={noticePad}>
            <Banner tone="danger" title={echoTitle} testid="floor-location-echo-mismatch"
              action={<Button variant="secondary" size="sm" onClick={retry} disabled={loading}>{retryLabel}</Button>}>
              {echoError}
            </Banner>
          </div>
        ) : null}
        {zoneError ? <EmptyState title={zoneTitle} body={zoneError} /> : null}
        {anchorPending ? <div role="status" style={{ padding: 18, fontSize: 13.5, color: T.soft }}>{tzLoadingMsg}</div>
          : scopedDay && !echoError && !zoneError && !boardDay ? <div role="status" style={{ padding: 18, fontSize: 13.5, color: T.soft }}>{dayLoadingMsg}</div>
          : refreshing ? <div role="status" aria-live="polite" style={{ padding: `${T.space[2]} ${T.space[4]}`, fontSize: 13.5, color: T.soft }}>{refreshingMsg}</div>
          : null}
        {linkingFailed ? (
          <div style={noticePad}>
            <Banner tone="warning" title={linkingFailTitle} testid="floor-event-linking-failed"
              action={<Button variant="secondary" size="sm" onClick={retry} disabled={loading}>{retryLabel}</Button>}>
              {linkingFailBody}
            </Banner>
          </div>
        ) : null}
        {linkNotice === "miss" ? (
          <div style={noticePad}>
            <Banner tone="warning" title={eventLinkTitle} onDismiss={() => setLinkNotice(null)} dismissLabel={t("common.dismiss", "Dismiss")} testid="floor-event-link-miss">
              {eventLinkBody}
            </Banner>
          </div>
        ) : null}
        {linkNotice === "notReady" ? (
          <div style={noticePad}>
            <Banner tone="info" title={notReadyNavTitle} onDismiss={() => setLinkNotice(null)} dismissLabel={t("common.dismiss", "Dismiss")} testid="floor-event-link-not-ready">
              {notReadyNavBody}
            </Banner>
          </div>
        ) : null}
        <div aria-busy={loading || catalogPending} style={{ position: "relative", display: "flex", flex: 1, minHeight: 0 }}>
          <iframe ref={frameRef} title={t("floor.calendar.title", "Floor Calendar")} src={boardSrc}
            onLoad={() => { /* ready message drives the first push; this is the reload fallback */ if (day) push(); }}
            style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "#F4F5F7" }} />
          {initialLoading ? (
            /* Over the board, not instead of it: the frame must stay mounted so its
             * ready handshake lands while the first day loads. */
            <div role="status" aria-busy="true" aria-live="polite" data-testid="floor-initial-loading"
              style={{ position: "absolute", inset: 0, display: "flex", padding: T.space[4], background: "#F4F5F7" }}>
              <span style={srOnly}>{catalogPending ? locationsLoadingMsg : initialLoadingMsg}</span>
              <Skeleton width="100%" height="100%" />
            </div>
          ) : null}
          {!locationReady && !catalogPending ? (
            /* No valid location: the board stays mounted underneath, covered, and
             * nothing is read. Each state says what is true and what to do. */
            <div data-testid="floor-location-gate"
              style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: T.space[3], padding: T.space[4], background: "#F4F5F7", overflowY: "auto" }}>
              {gate.kind === "denied" ? (
                <div data-testid="floor-location-denied">
                  <EmptyState title={t("floorCal.locationDeniedTitle", "Locations are unavailable for your role")}
                    body={t("floorCal.locationDeniedBody", "The Floor Calendar shows one location's day, and your role can't read this workspace's locations. Ask an administrator for location access.")} />
                </div>
              ) : null}
              {gate.kind === "error" ? (
                <Banner tone="danger" title={t("floorCal.locationLoadFailedTitle", "Locations couldn't be loaded")} testid="floor-location-failed"
                  action={<Button variant="secondary" size="sm" onClick={locGate.retry}>{retryLabel}</Button>}>
                  {t("floorCal.locationLoadFailedBody", "The calendar can't confirm which location to show until the location list loads. Retry, and if it keeps happening the locations service may be unavailable.")}
                </Banner>
              ) : null}
              {gate.kind === "empty" ? (
                <div data-testid="floor-location-empty">
                  <EmptyState title={t("floorCal.locationEmptyTitle", "No active locations yet")}
                    body={t("floorCal.locationEmptyBody", "The Floor Calendar shows one location's day. Add or activate a location in Administration › Locations.")} />
                </div>
              ) : null}
              {gate.kind === "unselected" ? (
                <Banner tone="info" title={t("floorCal.locationChooseTitle", "Choose a location")} testid="floor-location-choose">
                  {t("floorCal.locationChooseBody", "The Floor Calendar shows one location at a time. Choose the location to open.")}
                </Banner>
              ) : null}
              {gate.kind === "unavailable" ? (
                <Banner tone="warning" title={t("floorCal.locationUnavailableTitle", "The selected location isn't available")} testid="floor-location-unavailable">
                  {gate.reason === "inactive"
                    ? t("floorCal.locationInactiveBody", "That location is inactive, so its calendar isn't shown. Choose another location.")
                    : t("floorCal.locationUnknownBody", "That location isn't in this workspace's location list. Choose another location.")}
                </Banner>
              ) : null}
              {gate.kind === "unselected" || gate.kind === "unavailable" ? (
                <LocationPicker locations={gate.locations} value={null} onPick={locGate.select} label={locationLabel} placeholder={locationPlaceholder} />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Screen>
  );
}
